<template>
  <div class="animated fadeIn support-notifications-page">
    <b-row>
      <b-col cols="12">
        <b-card>
          <template #header>
            <div
              class="d-flex align-items-center justify-content-between flex-wrap"
            >
              <div>
                <h5 class="m-0">Notificações de suporte</h5>
                <small class="text-muted">
                  Atualizações não lidas dos seus chamados e atendimentos
                </small>
              </div>
              <b-button
                variant="outline-primary"
                size="sm"
                :disabled="loading"
                @click="loadNotifications"
              >
                <i class="fa fa-sync-alt mr-1"></i>
                Atualizar
              </b-button>
            </div>
          </template>

          <b-card-body>
            <div v-if="loading" class="text-center py-5">
              <b-spinner></b-spinner>
              <span class="ml-2">Carregando...</span>
            </div>

            <div
              v-else-if="groupedNotifications.length === 0"
              class="empty-state"
            >
              <i class="fa fa-check-circle fa-2x text-success"></i>
              <p class="mt-3 mb-0 text-muted">
                Você não possui notificações de suporte pendentes.
              </p>
            </div>

            <div v-else>
              <b-table
                :items="groupedNotifications"
                :fields="fields"
                responsive
                hover
                borderless
                thead-class="support-header"
                @row-clicked="openNotification"
              >
                <template #cell(protocol)="slotData">
                  <strong class="protocol-link">
                    {{ slotData.item.supportTicket.protocol }}
                  </strong>
                </template>

                <template #cell(module)="slotData">
                  {{ slotData.item.supportTicket.module || "-" }}
                </template>

                <template #cell(status)="slotData">
                  <b-badge
                    :variant="statusVariant(slotData.item.supportTicket.status)"
                  >
                    {{ statusLabel(slotData.item.supportTicket.status) }}
                  </b-badge>
                </template>

                <template #cell(eventLabel)="slotData">
                  {{ eventLabel(slotData.item.type) }}
                </template>

                <template #cell(count)="slotData">
                  <b-badge variant="secondary" pill>
                    {{ slotData.item.count }}
                  </b-badge>
                </template>

                <template #cell(createdAt)="slotData">
                  {{ formatDate(slotData.item.createdAt) }}
                </template>

                <template #cell(action)>
                  <i class="fa fa-external-link-alt text-muted"></i>
                </template>
              </b-table>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import NotificationType from "../../../../backend/models/NotificationType.ts";
import { markAsRead } from "../../api/notifications";
import { getSupportNotifications } from "../../api/support";

export default {
  name: "SupportNotificationsPage",
  data() {
    return {
      loading: false,
      notifications: [],
      fields: [
        { key: "protocol", label: "Protocolo" },
        { key: "module", label: "Módulo" },
        { key: "status", label: "Status" },
        { key: "eventLabel", label: "Evento" },
        { key: "count", label: "Quantidade" },
        { key: "createdAt", label: "Criado em" },
        { key: "action", label: "Ação", class: "text-center" },
      ],
      NotificationType,
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    groupedNotifications() {
      const grouped = {};

      for (const notification of this.notifications) {
        if (!notification.supportTicket || !notification.supportTicket._id) {
          continue;
        }

        const key = `${notification.supportTicket._id}|${notification.type}`;
        grouped[key] = grouped[key] || [];
        grouped[key].push(notification);
      }

      return Object.keys(grouped)
        .map((key) => {
          const items = grouped[key].sort((a, b) => b._id - a._id);
          const first = items[0];

          return {
            _id: first._id,
            type: first.type,
            createdAt: first.createdAt,
            supportTicket: first.supportTicket,
            count: items.length,
          };
        })
        .sort((a, b) => b._id - a._id);
    },
  },
  created() {
    this.loadNotifications();
  },
  methods: {
    async loadNotifications() {
      this.loading = true;

      try {
        const response = await getSupportNotifications();
        this.notifications = Array.isArray(response && response.data)
          ? response.data
          : [];
      } catch (error) {
        this.notifications = [];
      } finally {
        this.loading = false;
      }
    },
    eventLabel(type) {
      const labels = {
        [NotificationType.NEW_SUPPORT_TICKET]: "Novo chamado",
        [NotificationType.NEW_SUPPORT_MESSAGE]: "Nova mensagem",
        [NotificationType.NEW_SUPPORT_STATUS]: "Atualização de status",
      };

      return labels[type] || "Nova notificação";
    },
    statusLabel(status) {
      const labels = {
        open: "Aberto",
        in_progress: "Em atendimento",
        waiting_customer: "Aguardando cliente",
        resolved: "Resolvido",
        closed: "Fechado",
      };

      return labels[status] || status;
    },
    statusVariant(status) {
      const variants = {
        open: "warning",
        in_progress: "primary",
        waiting_customer: "info",
        resolved: "success",
        closed: "secondary",
      };

      return variants[status] || "secondary";
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      return new Date(value).toLocaleString("pt-BR");
    },
    async openNotification(notification) {
      await markAsRead(notification);
      if (this.profile && this.profile.role === "root") {
        this.$router.push("/suporte");
        return;
      }

      this.$router.push(`/suporte/${notification.supportTicket._id}`);
    },
  },
};
</script>

<style scoped>
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
}

.protocol-link {
  color: #1f4fa3;
}
</style>
